import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, InputGroup, ListGroup, ModalFooter, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faLink, faSearch, faStar, faTimes, faUnlink } from '@fortawesome/free-solid-svg-icons';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import Swal from 'sweetalert2';
import ModalDommus from '../../../../components/Modal';
import DommusLoadingArea from '../../../../components/DommusLoading/DommusLoadingArea';
import { DommusAlert } from '../../../../components/DommusAlert';
import { cpfMask, celularMask } from '../../../../components/InputTextMask';
import { successToast, errorToast } from '../../../../components/DommusToast';
import formatDommus from '../../../../helpers/format';
import { AtendimentoContext } from '../../../../contexts/AtendimentoContext';
import caAPI from '../../../../services/caAPI';

/**
 * Gerencia os processos vinculados a um atendimento (principal + adicionais):
 * - Lista os processos vinculados e permite acessá-los ou desvinculá-los.
 * - Permite vincular novos processos reaproveitando a busca de processos existente.
 *
 * O componente é controlado: os estados de abertura dos modais e a contagem de
 * processos adicionais são gerenciados pelo componente pai (MenuDetalhes).
 */
export default function ProcessosVinculados({
  openLista,
  setOpenLista,
  openVincular,
  setOpenVincular,
  onAtualizarContagem,
  onSelecionarProcessoVinculado,
  onRegistrarDesvincular,
}) {
  const { atendimentoAtivo, setAtendimentoAtivo, handleAdiconaLista } = useContext(AtendimentoContext);
  const [listaVinculados, setListaVinculados] = useState([]);
  const [infoBusca, setInfoBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [processosSelecionados, setProcessosSelecionados] = useState([]);
  const { promiseInProgress: isLoadingBusca } = usePromiseTracker({ area: 'buscarProcessosVincularArea' });

  const idsVinculadosAtivos = listaVinculados.map((item) => Number(item.id_processo));

  const aplicarVinculados = useCallback((res) => {
    const lista = Array.isArray(res.data) ? res.data : [];
    setListaVinculados(lista);
    const quantidadeAdicionais = lista.filter((item) => String(item.principal) !== '1').length;
    if (onAtualizarContagem) {
      onAtualizarContagem(quantidadeAdicionais);
    }
    return lista;
  }, [onAtualizarContagem]);

  const carregarVinculados = useCallback(() => {
    if (!atendimentoAtivo?.id) {
      return;
    }
    trackPromise(
      caAPI.atendimento.processo.listarVinculados(atendimentoAtivo.id).then(aplicarVinculados).catch(() => {
        setListaVinculados([]);
        if (onAtualizarContagem) {
          onAtualizarContagem(0);
        }
      })
    );
  }, [atendimentoAtivo?.id, onAtualizarContagem, aplicarVinculados]);

  useEffect(() => {
    carregarVinculados();
  }, [carregarVinculados, atendimentoAtivo?.processo]);

  // Carrega candidatos iniciais (processos do contato) ao abrir o modal de vínculo.
  useEffect(() => {
    if (openVincular) {
      setProcessosSelecionados([]);
      setInfoBusca('');
      if (atendimentoAtivo?.contato_destino && String(atendimentoAtivo.contato_destino).trim().length >= 5) {
        buscarProcessos(atendimentoAtivo.contato_destino);
      } else {
        setResultadosBusca([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openVincular]);

  function buscarProcessos(info) {
    if (!info || String(info).trim().length < 5) {
      Swal.fire({
        titleText: 'Atenção',
        text: 'Digite no mínimo 5 caracteres para buscar.',
        icon: 'info'
      });
      return;
    }
    setResultadosBusca([]);
    trackPromise(
      caAPI.atendimento.processo.buscarPorInfo(info).then((res) => {
        const encontrados = (Array.isArray(res.data) ? res.data : []).filter((item) => item.processo_id);
        setResultadosBusca(encontrados);
      }).catch(() => {
        Swal.fire('Ooops ...', 'Nenhum processo encontrado', 'error');
      }), 'buscarProcessosVincularArea'
    );
  }

  function alternarSelecao(idProcesso) {
    const id = Number(idProcesso);
    setProcessosSelecionados((atuais) => (
      atuais.includes(id) ? atuais.filter((item) => item !== id) : [...atuais, id]
    ));
  }

  function handleVincularSelecionados() {
    if (!processosSelecionados.length) {
      Swal.fire({
        titleText: 'Atenção',
        text: 'Selecione ao menos um processo para vincular.',
        icon: 'info'
      });
      return;
    }
    trackPromise(
      caAPI.atendimento.processo.vincular(atendimentoAtivo.id, processosSelecionados).then(() => {
        successToast.fire({ text: 'Processo(s) vinculado(s) com sucesso!' });
        setOpenVincular(false);
        setProcessosSelecionados([]);
        setResultadosBusca([]);
        carregarVinculados();
      }).catch((err) => {
        errorToast.fire({ text: 'Erro ao vincular processo(s): ' + err });
      }), 'buscarProcessosVincularArea'
    );
  }

  function handleAcessarProcesso(idProcesso) {
    trackPromise(
      caAPI.atendimento.processo.buscaPorId(idProcesso).then((res) => {
        if (onSelecionarProcessoVinculado) {
          onSelecionarProcessoVinculado(res.data, idProcesso);
        }
        setOpenLista(false);
      }).catch(() => {
        Swal.fire('Ooops ...', 'Processo não encontrado', 'error');
      })
    );
  }

  const atualizarPrincipalNoAtendimento = useCallback((lista) => {
    const itemPrincipal = lista.find((item) => String(item.principal) === '1');
    const novoProcessoPrincipal = itemPrincipal ? Number(itemPrincipal.id_processo) : null;
    const processoAtual = atendimentoAtivo.processo ? Number(atendimentoAtivo.processo) : null;
    if (processoAtual !== novoProcessoPrincipal) {
      const novoAtendimentoAtivo = Object.assign({}, atendimentoAtivo, { processo: novoProcessoPrincipal });
      setAtendimentoAtivo(novoAtendimentoAtivo);
      handleAdiconaLista(novoAtendimentoAtivo);
    }
  }, [atendimentoAtivo, setAtendimentoAtivo, handleAdiconaLista]);

  const executarDesvincular = useCallback((idProcesso) => {
    setOpenLista(false);
    return caAPI.atendimento.processo.desvincular(atendimentoAtivo.id, idProcesso).then((res) => {
      const lista = aplicarVinculados(res);
      atualizarPrincipalNoAtendimento(lista);
      successToast.fire({ text: 'Processo desvinculado com sucesso!' });
      return lista;
    }).catch((err) => {
      errorToast.fire({ text: 'Erro ao desvincular processo: ' + err });
      return Promise.reject(err);
    });
  }, [atendimentoAtivo, aplicarVinculados, atualizarPrincipalNoAtendimento, setOpenLista]);

  useEffect(() => {
    if (onRegistrarDesvincular) {
      onRegistrarDesvincular(executarDesvincular);
    }
  }, [onRegistrarDesvincular, executarDesvincular]);

  function handleDefinirPrincipal(idProcesso) {
    confirmar(
      'Definir como processo principal?',
      'Este processo passará a ser o principal deste atendimento.'
    ).then((result) => {
      if (!result.value) {
        return;
      }
      setOpenLista(false);
      trackPromise(
        caAPI.atendimento.processo.definirPrincipal(atendimentoAtivo.id, idProcesso).then((res) => {
          const lista = aplicarVinculados(res);
          atualizarPrincipalNoAtendimento(lista);
          successToast.fire({ text: 'Processo principal atualizado com sucesso!' });
        }).catch((err) => {
          errorToast.fire({ text: 'Erro ao definir processo principal: ' + err });
        }), 'loading-detalhes-processo'
      );
    });
  }

  function handleDesvincular(idProcesso) {
    confirmar(
      'Desvincular processo do atendimento?',
      'Tem certeza que deseja desvincular este processo deste atendimento?'
    ).then((result) => {
      if (!result.value) {
        return;
      }
      trackPromise(executarDesvincular(idProcesso).catch(() => {}), 'loading-detalhes-processo');
    });
  }

  return (
    <>
      <ModalDommus
        open={openLista}
        size={'md'}
        close={() => setOpenLista(false)}
        titulo={'Processos Vinculados'}
      >
        <div style={{ minHeight: '120px' }}>
          <ListGroup>
            {listaVinculados.map((item) => {
              const ehPrincipal = String(item.principal) === '1';
              return (
                <ListGroup.Item
                  key={item.id_atendimento_processo}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <FontAwesomeIcon icon={faLink} color="#777" />
                    <span>Processo Nº {formatDommus.zeroPad(item.id_processo, 5)}</span>
                    {ehPrincipal && (
                      <span className="badge badge-primary">Principal</span>
                    )}
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                    {!ehPrincipal &&
                      <OverlayTrigger placement="top" overlay={<Tooltip id={`principal-${item.id_processo}`}>Definir como principal</Tooltip>}>
                        <FontAwesomeIcon
                          icon={faStar}
                          color="#f0ad4e"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleDefinirPrincipal(item.id_processo)}
                        />
                      </OverlayTrigger>
                    }
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`abrir-${item.id_processo}`}>Ver informações</Tooltip>}>
                      <FontAwesomeIcon
                        icon={faEye}
                        color="#265d9c"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleAcessarProcesso(item.id_processo)}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`desvincular-${item.id_processo}`}>Desvincular</Tooltip>}>
                      <FontAwesomeIcon
                        icon={faUnlink}
                        color="#dc3545"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDesvincular(item.id_processo)}
                      />
                    </OverlayTrigger>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
          {!listaVinculados.length && (
            <DommusAlert title={'Atenção!'} message={'Nenhum processo vinculado a este atendimento.'} />
          )}
        </div>
      </ModalDommus>

      <ModalDommus
        open={openVincular}
        size={'lg'}
        close={() => setOpenVincular(false)}
        titulo={'Vincular processos'}
      >
        <div>
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              buscarProcessos(infoBusca);
            }}
          >
            <Form.Label>Buscar por Nome/Tel/Email/CPF:</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                value={infoBusca}
                placeholder="Nome/Tel/Email/CPF"
                onChange={(event) => setInfoBusca(event.target.value)}
              />
              <InputGroup.Append>
                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-buscar-processo">Procurar</Tooltip>}>
                  <Button type="submit" style={{ zIndex: 0 }}>
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </OverlayTrigger>
              </InputGroup.Append>
            </InputGroup>
          </Form>

          <div style={{ marginTop: '15px', minHeight: '120px' }}>
            <DommusLoadingArea area="buscarProcessosVincularArea" fundo={'#fff'} />
            <ListGroup>
              {resultadosBusca.map((processo, index) => {
                const idProcesso = Number(processo.processo_id);
                const jaVinculado = idsVinculadosAtivos.includes(idProcesso);
                const selecionado = processosSelecionados.includes(idProcesso);
                return (
                  <ListGroup.Item
                    key={`${idProcesso}-${index}`}
                    action={!jaVinculado}
                    active={selecionado}
                    disabled={jaVinculado}
                    onClick={() => !jaVinculado && alternarSelecao(idProcesso)}
                  >
                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selecionado}
                        disabled={jaVinculado}
                        onChange={() => alternarSelecao(idProcesso)}
                        onClick={(event) => event.stopPropagation()}
                      />
                      <div>
                        <div>
                          {formatDommus.zeroPad(idProcesso, 5)}
                          {processo.proponente_encontrado_nome ? ` - ${processo.proponente_encontrado_nome}` : ''}
                          {jaVinculado ? ' (já vinculado)' : ''}
                        </div>
                        {processo.proponente_encontrado_cpf && (
                          <div style={{ fontSize: '0.85rem', color: '#777' }}>
                            {cpfMask(processo.proponente_encontrado_cpf)}
                            {processo.telefone ? ` - ${celularMask(processo.telefone)}` : ''}
                          </div>
                        )}
                        {processo.email && (
                          <div style={{ fontSize: '0.85rem', color: '#777' }}>{processo.email}</div>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
            {!isLoadingBusca && !resultadosBusca.length && (
              <DommusAlert title={'Atenção!'} message={'Nenhum processo encontrado para vincular.'} />
            )}
          </div>

          <ModalFooter className="d-flex justify-content-between">
            <strong>Selecionados: {processosSelecionados.length}</strong>
            <div>
              <Button variant="success" type="button" className="mx-2" onClick={handleVincularSelecionados}>
                <FontAwesomeIcon icon={faLink} /> Vincular
              </Button>
              <Button variant="secondary" type="button" className="mx-2" onClick={() => setOpenVincular(false)}>
                <FontAwesomeIcon icon={faTimes} /> Cancelar
              </Button>
            </div>
          </ModalFooter>
        </div>
      </ModalDommus>
    </>
  );
}

function confirmar(titleText, text) {
  return Swal.fire({
    titleText,
    text,
    icon: 'question',
    showCloseButton: false,
    showCancelButton: true,
    focusConfirm: false,
    confirmButtonText: 'Sim!',
    cancelButtonText: 'Não!',
    customClass: {
      confirmButton: 'btn btn-primary mr-3',
      cancelButton: 'btn btn-danger'
    },
    buttonsStyling: false
  });
}
